import Splide from'@splidejs/splide';

customElements.define('carousel-splide', class extends HTMLElement {
    constructor() {
        super();
 
        this.type = this.getAttribute('type');
        this.options = this.getOptions(this.type);

        this.build();
    }

    build() {
        let container = this.querySelector('.carousel__container'),
        list = this.querySelector('.carousel__list');

        if (container && list) {
            let track = document.createElement('div');

            this.classList.add('splide');
            this.classList.add('carousel');
            track.classList.add('splide__track');
            list.classList.add('splide__list');
            list.classList.remove('carousel__list');

            this.querySelectorAll('.carousel__item').forEach(item => {
                item.classList.add('splide__slide');
            })


            container.appendChild(track);
            track.appendChild(list);

            const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (prefersReduced) {
                this.options = { ...this.options, speed: 0, rewindSpeed: 0 };
            }

            let splide = new Splide(this, this.options);

            const type = this.type;

            splide.on('mounted', function () {
                document.querySelectorAll('.splide__arrow svg').forEach(svg => {
                    svg.setAttribute('viewBox', '0 0 16 16');
                    svg.setAttribute('aria-hidden', 'true');
                    svg.setAttribute('focusable', 'false');
                });

                const splideEl = splide.root;
                const prev = splideEl.querySelector('.splide__arrow--prev');
                const next = splideEl.querySelector('.splide__arrow--next');
                if (prev && !prev.getAttribute('aria-label')) prev.setAttribute('aria-label', 'Previous slide');
                if (next && !next.getAttribute('aria-label')) next.setAttribute('aria-label', 'Next slide');
            });

            // Progress bar (Figma shows a track + active fill instead of dots).
            if (type === 'product-carousel' || type === 'inspiration') {
                this.addProgressBar(splide);
            }

            if (type === 'inspiration') {
                this.trackNeighbours(splide);
            }

            if (type === 'product-carousel') {
                // When the track moves, the pointer can end up off a card without
                // a mouseleave firing, which would strand it in the zoomed state.
                // Clear the hover class on move so the image eases back out.
                const clearHover = () => {
                    this.querySelectorAll('.carousel-card.is-hovered').forEach(card => {
                        card.classList.remove('is-hovered');
                    });
                };

                splide.on('move', clearHover);

                this.querySelectorAll('.carousel-card').forEach(card => {
                    card.addEventListener('mouseenter', () => card.classList.add('is-hovered'));
                    card.addEventListener('mouseleave', () => card.classList.remove('is-hovered'));
                });
            }

            splide.mount();
        }
    }

    // Inspiration carousel: inactive slides shrink toward the active one
    // (transform-origin on the side facing it), so CSS needs each slide's side
    // *as soon as* a move starts — paired with updateOnMove, which moves
    // is-active at the same moment.
    //
    // Every slide left of the target is "is-left", every slide right of it
    // "is-right". The incoming slide is marked with the side it's arriving
    // from, so it grows out from the edge it was already hugging.
    //
    // Splide always animates to the *real* slide (`newIndex`) — when looping
    // it first jumps the track so that slide is reachable — so sides are
    // measured from newIndex, and the direction comes from dest vs prev
    // (dest runs past the ends while wrapping, so it's correct there too).
    // All of this runs in the same task as Splide's jump, so nothing paints
    // in between.
    trackNeighbours(splide) {
        const markSides = (target, forward = true) => {
            splide.Components.Slides.forEach(({ slide, index }) => {
                const left = index === target ? !forward : index < target;
                const right = index === target ? forward : index > target;
                slide.classList.toggle('is-left', left);
                slide.classList.toggle('is-right', right);
            });
        };

        splide.on('mounted shifted', () => markSides(splide.index));
        splide.on('move', (newIndex, prevIndex, destIndex) => markSides(newIndex, destIndex > prevIndex));
    }

    // Splide has no built-in progress bar, so build one: a track with a fill
    // whose width tracks the current slide. Decorative — the arrows and slide
    // announcements carry the state for assistive tech.
    addProgressBar(splide) {
        const bar = document.createElement('div');
        bar.className = 'carousel__progress';
        bar.setAttribute('aria-hidden', 'true');
        bar.innerHTML = '<div class="carousel__progress-fill"></div>';
        this.appendChild(bar);

        const fill = bar.querySelector('.carousel__progress-fill');

        const updateProgress = () => {
            const end = splide.Components.Controller.getEnd() + 1;
            const rate = Math.min((splide.index + 1) / end, 1);
            fill.style.width = String(rate * 100) + '%';
        };

        splide.on('mounted move resized', updateProgress);
    }

    getOptions(type) {
        const common = {
            classes: {
                arrow: 'splide__arrow carousel__arrow',
                arrows: 'splide__arrows carousel__arrows',
                next: 'splide__arrow--next carousel__arrow--next',
                prev: 'splide__arrow--prev carousel__arrow--prev',
                pagination: 'splide__pagination carousel__pagination',
                page: 'splide__pagination__page carousel__pagination__page',
            }
        }

        const options = {
            'testimonial': {
                type: 'slide',
                arrowPath: 'M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z',
                pagination: true,
                perPage: 1,
                width: '100%',
                breakpoints: {
                    767: { arrows: false }
                }
            },
            'featured': {
                type: 'slide',
                arrowPath: 'M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z',
                pagination: true,
                perPage: 1,
                breakpoints: {
                    767: { arrows: false }
                }
            },
            'offset': {
                type: 'slide',
                arrowPath: 'M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z',
                pagination: false,
                perPage: 2,
                perMove: 1,
                gap: '1.5rem',
                padding: { right: '4rem' },
                omitEnd: true,
                trimSpace: false,
                width: '100%',
                breakpoints: {
                    1024: { perPage: 1, padding: { right: '6rem' } },
                    767: { perPage: 1, arrows: false, padding: { right: '2rem' } }
                }
            },
            // Figma 7665:48238 — centred 1000px (16:9) active slide with the
            // neighbours peeking either side; CSS shrinks the neighbours toward
            // the active slide. Mobile: 330px portrait slides, no arrows.
            'inspiration': {
                type: 'loop',
                arrowPath: 'M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z',
                focus: 'center',
                fixedWidth: '62.5rem',    // 1000px
                gap: 0,
                pagination: false,
                perMove: 1,
                trimSpace: false,
                updateOnMove: true,       // resize slides during the move, not after
                breakpoints: {
                    1199: { fixedWidth: '70%' },
                    767:  { fixedWidth: '20.625rem', arrows: false } // 330px
                }
            },
            // Figma 6688:32984 — 400px cards with a 32px gap, peeking past the
            // right edge. Progress bar instead of dot pagination.
            'product-carousel': {
                type: 'slide',
                arrowPath: 'M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z',
                perPage: 3,
                perMove: 1,
                gap: '2rem',              // 32px
                pagination: false,
                omitEnd: true,
                trimSpace: false,
                padding: { right: '7rem' }, // let the next card peek
                breakpoints: {
                    1024: { perPage: 2, gap: '1.5rem', padding: { right: '5rem' } },
                    767:  { perPage: 1, gap: '1rem', arrows: false, padding: { right: '3rem' } }
                }
            },
            'reviews': {
                type: 'slide',
                arrowPath: 'M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z',
                pagination: true,
                padding: { right: '5.234375rem' },
                omitEnd: true,
                trimSpace: false,
                perPage: 3,
                perMove: 3,
                gap: '1.5rem',
                width: '100%',
                breakpoints: {
                    1024: {
                        perPage: 1,
                        perMove: 1,
                        arrows: true,
                        gap: '1.5rem',
                        padding: { right: '10.46875rem' },
                    },
                    767: { 
                        perPage: 1,
                        perMove: 1,
                        arrows: false,
                        gap: '0.5rem',
                        padding: { right: '1rem' },
                    }
                }
            }
        };

        if (options[type]) {
            return {...common, ...options[type]};
        }

        return {...common};
    }
});